import { FormikProps } from "formik";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { applyApiErrors, VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { errorEntryType } from "@/core/api";
import { IRole, postRole } from "@/data/roles";
import { ITypeaheadProps } from "@/eworkitComponents";
import { isErrorVisible, withFormikField } from "@/formikComponents";
import { TypeaheadRoles } from "@/views/components/Typeaheads";

const ROLE_NAME_MAX_LENGTH = 100;

export interface IRoleCreatableTypeaheadProps extends Omit<ITypeaheadProps, "loadOptions"> {
  name: string;
  form: FormikProps<any>;
  filterRoles?: string[];
}

export const RoleCreatableTypeahead: FC<IRoleCreatableTypeaheadProps> = ({
  name,
  value,
  form,
  testName,
  onChange,
  invalid,
  ...rest
}) => {
  const safeName = name ?? "role.role";
  const isFieldInvalid = invalid ?? !!isErrorVisible(form, safeName);
  const { t } = useTranslation("session");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const formatCreateLabel = useCallback((label: string) => {
    return `${t("CONSULTANT_PROFILES.DETAIL.ROLE.ADD_NEW_OPTION_PLACEHOLDER")} ${label}`;
  }, []);

  const getNewOptionData = useCallback((_inputValue: string, optionLabel: string) => {
    return { name: optionLabel } as any;
  }, []);

  const onCreateOption = useCallback(
    async (inputValue: string) => {
      setIsDisabled(true);

      try {
        const createdOption = await postRole({}, { name: inputValue.trim() });
        onChange?.(createdOption);
      } catch (errorResponse) {
        const errorEntry = errorEntryType(errorResponse.payload);

        applyApiErrors(form, { ...errorEntry, errors: errorEntry.errors.map(ef => ({ ...ef, field: name })) });
        form.setFieldTouched(name);
      }

      setIsDisabled(false);
    },
    [form],
  );

  const isNewOptionValid = useCallback((inputValue: string, _: IRole[], options: IRole[]): boolean => {
    return (
      !!inputValue &&
      !options.filter((r: IRole) => r.name.trim().toLowerCase() === inputValue.trim().toLowerCase()).length
    );
  }, []);

  return (
    <>
      <TypeaheadRoles
        name={name}
        value={value}
        testName={testName}
        valueKey="id"
        labelKey="name"
        isDisabled={isDisabled}
        onChange={onChange}
        isCreatable
        formatCreateLabel={formatCreateLabel}
        onCreateOption={onCreateOption}
        getNewOptionData={getNewOptionData}
        isValidNewOption={isNewOptionValid}
        invalid={isFieldInvalid}
        {...({ createOptionPosition: "first" } as any)}
        {...rest}
      />
      <FormikError
        name={name}
        data-test={`error-${testName}`}
        overrideMessages={{
          [VALIDATION_TYPES.ALREADY_EXISTS]: t("CONSULTANT_PROFILES.DETAIL.ROLE.ALREADY_EXISTS_ERROR_MESSAGE"),
          INVALID_SIZE: t("CONSULTANT_PROFILES.DETAIL.ROLE.INVALID_SIZE_ERROR_MESSAGE", {
            value: ROLE_NAME_MAX_LENGTH,
          }),
        }}
      />
    </>
  );
};

export const FormikRoleCreatableTypeahead = withFormikField(RoleCreatableTypeahead);
